// Workspace JWT provider for @symbo.ls/sdk WorkspaceProjectService.
//
// The wrapper at next.api.symbols.app/workspace authenticates each
// request by reading `Authorization: Bearer <jwt>`. The wrapper extracts
// `workspace_id` (or `app_metadata.workspace_id`) from the JWT to scope
// RLS server-side. The Symbols SDK's own token (`/core/auth/login`) does
// NOT carry that claim — only the GOVERNANCE Supabase JWT does. The
// federation layer (sdk-bridge `loginAll`) signs the user into BOTH
// systems and writes the governance JWT to localStorage under
// `sb-<governance-ref>-auth-token`; we read it from there directly so
// the workspace wrapper accepts our calls.
//
// SDK boot wires this via context.workspaceProjectTokenProvider, so every
// `sdk.workspaceProject.*` call inherits the token automatically.

import { governanceSessionAccessToken } from '../../shared/functions/supabaseConfig.js'
import { readGovernanceClaims } from '../../shared/functions/diagnoseAuth.js'

let _cachedToken = null
let _cachedAt = 0
let _missingWarned = false
let _expiredWarned = false
const CACHE_TTL_MS = 30_000

export async function workspaceTokenProvider() {
  const now = Date.now()
  if (_cachedToken && now - _cachedAt < CACHE_TTL_MS) {
    return _cachedToken
  }

  try {
    const token = governanceSessionAccessToken()
    if (!token) {
      if (!_missingWarned) {
        _missingWarned = true
        console.warn(
          '[workspaceTokenProvider] No governance Supabase JWT in localStorage — ' +
          '/workspace/* calls will 401 with "missing bearer token". ' +
          'The Symbols SDK token does NOT carry the workspace_id claim required by the wrapper. ' +
          'Sign in via the federated login flow (loginAll) to write sb-<governance-ref>-auth-token. ' +
          'Run `__auth.diagnose()` for a full snapshot.'
        )
      }
      return null
    }
    const expSec = readGovernanceClaims().exp
    if (expSec && expSec * 1000 < now) {
      if (!_expiredWarned) {
        _expiredWarned = true
        console.warn(
          '[workspaceTokenProvider] Governance Supabase JWT in localStorage is expired — ' +
          `expired at ${new Date(expSec * 1000).toISOString()}. ` +
          '/workspace/* calls will 401. Sign out + sign in again to refresh, or rely on Supabase auth-js auto-refresh. ' +
          'Run `__auth.diagnose()` for a full snapshot.'
        )
      }
      return null
    }
    _missingWarned = false
    _expiredWarned = false
    _cachedToken = token
    _cachedAt = now
    return _cachedToken
  } catch (err) {
    console.warn('[workspaceTokenProvider] unexpected error:', err?.message || err)
    return null
  }
}

export function clearWorkspaceTokenCache() {
  _cachedToken = null
  _cachedAt = 0
  _missingWarned = false
  _expiredWarned = false
}
